// web/src/screens/admin/modal.ts
//
// Minimal <dialog>-based modal helper for the admin UI. The codebase had no
// modal primitive — only window.confirm/prompt and absolutely-positioned
// overlays in card.ts. A real <dialog> opened with showModal() puts the
// content in the top layer and traps focus, so keystrokes can't leak to a
// parent <form> (the admin edit screens wrap their fields in a submit-to-save
// <form>).
//
// openModal({title}) returns { dialog, content, close }:
//   - `content` is a padded <div> the caller fills (typically with its own
//     <form> so Enter submits and Escape cancels — the standard pattern).
//   - The dialog closes on: the × button, Escape (native `cancel` event),
//     a backdrop click (click on the <dialog> itself, possible because the
//     dialog has zero padding and the content div fills it), or close().
//   - A single `close` event listener removes the <dialog> from the DOM, so
//     the node never lingers. Callers that need to react to close (e.g. to
//     resolve a promise) should add their own `close` listener on `dialog`.
//
// All caller-supplied / API text must be set via .textContent (admin XSS
// lock) — this module sets the title via .textContent.

const BACKDROP_STYLE_ID = "admin-modal-backdrop-style";

function ensureBackdropStyle(): void {
  if (document.getElementById(BACKDROP_STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = BACKDROP_STYLE_ID;
  s.textContent = "dialog[data-admin-modal]::backdrop { background: rgba(15, 23, 42, 0.42); }";
  document.head.appendChild(s);
}

export interface ModalHandle {
  dialog: HTMLDialogElement;
  content: HTMLElement;
  close(): void;
}

export function openModal(opts: { title: string }): ModalHandle {
  ensureBackdropStyle();

  const dialog = document.createElement("dialog");
  dialog.setAttribute("data-admin-modal", "1");
  dialog.setAttribute(
    "style",
    "padding: 0; border: 1px solid #d1d5db; border-radius: 8px; max-width: 560px; width: calc(100vw - 32px); box-shadow: 0 10px 40px rgba(0,0,0,0.25); font-family: system-ui, -apple-system, sans-serif; color: #111827; background: white;",
  );

  const header = document.createElement("div");
  header.setAttribute(
    "style",
    "display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid #e5e7eb;",
  );
  const titleEl = document.createElement("span");
  titleEl.textContent = opts.title; // textContent
  titleEl.setAttribute("style", "font-size: 15px; font-weight: 600; flex: 1;");
  const closeX = document.createElement("button");
  closeX.type = "button";
  closeX.textContent = "×";
  closeX.setAttribute("aria-label", "Close");
  closeX.setAttribute(
    "style",
    "border: 0; background: transparent; font-size: 22px; line-height: 1; cursor: pointer; color: #6b7280; padding: 0 4px;",
  );
  header.append(titleEl, closeX);

  const content = document.createElement("div");
  content.setAttribute("style", "padding: 16px;");

  dialog.append(header, content);
  document.body.appendChild(dialog);

  const close = (): void => { if (dialog.open) dialog.close(); };

  closeX.addEventListener("click", close);
  // Backdrop click: the <dialog> has padding:0 and `content` fills it, so a
  // click whose target is the dialog element itself is a click on the
  // backdrop region.
  dialog.addEventListener("click", (e) => { if (e.target === dialog) close(); });
  // Escape is handled natively (fires `cancel` then `close`). The `close`
  // listener below covers every path (× / Escape / backdrop / programmatic).
  dialog.addEventListener("close", () => { dialog.remove(); });

  dialog.showModal();

  return { dialog, content, close };
}
